import { type JSX, useEffect, useRef, useState } from 'react';
import { useServerWrite } from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { TicketPlusClaimed } from '../../common/components/ticketplus/TicketPlusClaimed';
import { QuickCheckoutFlowTicketApproved } from './QuickCheckoutFlowTicketApproved';
import { QuickCheckoutFlowTicketBooked } from './QuickCheckoutFlowTicketBooked';
import { QuickCheckoutFlowTicketNotFound } from './QuickCheckoutFlowTicketNotFound';
import { QuickCheckoutFlowTicketPaymentNotAllowed } from './QuickCheckoutFlowTicketPaymentNotAllowed';
import { QuickCheckoutFlowTicketScanned } from './QuickCheckoutFlowTicketScanned';
import { QuickCheckoutPaymentCallback } from './QuickCheckoutPaymentCallback';
import {
    FlowState,
    getZoneInfoFromFlowState,
    PaymentModalState,
    ZoneInfo,
} from './QuickCheckoutTypes';
import { useParams, Outlet, useOutletContext } from 'react-router';
import { TicketPlusTooManyApprovedTickets } from '../../common/components/ticketplus/TicketPlusTooManyApprovedTickets';
import { PaymentMethodEnum } from '../../api/Http.ts';
import { PayStationPaymentReceiptView } from '../../common/components/ticketplus/PayStationPaymentReceiptView.tsx';
import { QuickCheckoutFlowTicketExitedForFree } from './QuickCheckoutFlowTicketExitedForFree.tsx';
import { isRunningInNativeWrapper } from '../../Native.ts';

const QuickCheckoutFlow = ({
    flowState,
    refetchFlowState,
    paymentModalState,
    onPaymentStateModalConfirm,
}: {
    flowState: FlowState;
    refetchFlowState: () => void;
    paymentModalState?: PaymentModalState;
    onPaymentStateModalConfirm: () => void;
}): JSX.Element => {
    switch (flowState.flowState) {
        case 'PAY_STATION_RECEIPT':
            return (
                <PayStationPaymentReceiptView
                    ticketReceipt={flowState.ticketReceipt}
                    hasPaidPayment={flowState.hasPaidPayment}
                    hasDownloadableReceipt={flowState.hasDownloadableReceipt}
                    showTicketPlusUnavailableMessage={isRunningInNativeWrapper()}
                />
            );
        case 'TICKET_SCANNED':
        case 'ALIAS_PENDING':
            return (
                <QuickCheckoutFlowTicketScanned
                    {...flowState}
                    paymentModalState={paymentModalState}
                    onPaymentStateModalConfirm={onPaymentStateModalConfirm}
                    refetchFlowState={refetchFlowState}
                />
            );
        case 'TICKET_APPROVED':
            return (
                <QuickCheckoutFlowTicketApproved
                    {...flowState}
                    refetchFlowState={refetchFlowState}
                    paymentModalState={paymentModalState}
                    onPaymentStateModalConfirm={onPaymentStateModalConfirm}
                />
            );
        case 'TICKET_BOOKED':
            return <QuickCheckoutFlowTicketBooked flowState={flowState} />;
        case 'TICKET_CLAIMED':
            return <TicketPlusClaimed />;
        case 'TOO_MANY_APPROVED_TICKETS':
            return (
                <TicketPlusTooManyApprovedTickets
                    activePaymentType={PaymentMethodEnum.DIRECT}
                />
            );
        case 'TICKET_PAYMENT_NOT_ALLOWED':
            return <QuickCheckoutFlowTicketPaymentNotAllowed />;
        case 'TICKET_EXITED_FOR_FREE':
            return <QuickCheckoutFlowTicketExitedForFree />;
        case 'TICKET_NOT_FOUND':
            return <QuickCheckoutFlowTicketNotFound />;
    }
};

export const QuickCheckoutTicket = () => {
    const { ticketId, paymentAttemptId } = useParams();
    const [flowState, writeFlowState] = useServerWrite<
        { ticketId: string; hasPaymentAttempt: boolean },
        FlowState,
        unknown
    >(() => ({
        url: `/ui-api/quickcheckout/ticket/claim`,
    }));
    const refetchFlowState = () =>
        writeFlowState({
            ticketId: ticketId ?? '',
            hasPaymentAttempt: !!paymentAttemptId,
        });
    const hasWrittenFlowState = useRef(false);

    useEffect(() => {
        if (hasWrittenFlowState.current) {
            return;
        }
        if (ticketId) {
            writeFlowState({
                ticketId: ticketId,
                hasPaymentAttempt: !!paymentAttemptId,
            });
            hasWrittenFlowState.current = true;
        }
    }, [paymentAttemptId, ticketId, writeFlowState]);

    const [paymentModalState, setPaymentModalState] =
        useState<PaymentModalState>();

    const zoneInfo = getZoneInfoFromFlowState(flowState);

    if (
        ticketId !== undefined &&
        paymentAttemptId !== undefined &&
        paymentAttemptId.length > 0
    ) {
        return (
            <QuickCheckoutPaymentCallback
                paymentAttemptId={paymentAttemptId}
                ticketId={ticketId}
                onPaymentSuccessful={() => {
                    if (paymentModalState != PaymentModalState.SUCCESSFUL) {
                        setPaymentModalState(PaymentModalState.SUCCESSFUL);
                        refetchFlowState();
                    }
                }}
                onPaymentAborted={() => {
                    setPaymentModalState(PaymentModalState.ABORTED);
                }}
                onPaymentRejected={() => {
                    setPaymentModalState(PaymentModalState.FAILED);
                }}
            />
        );
    }

    return flowState.status === 'success' && ticketId !== undefined ? (
        <>
            <QuickCheckoutFlow
                flowState={flowState.data}
                refetchFlowState={refetchFlowState}
                paymentModalState={paymentModalState}
                onPaymentStateModalConfirm={() =>
                    setPaymentModalState(undefined)
                }
            />
            <ZoneInfoOutlet context={{ zoneInfo }} />
        </>
    ) : null;
};

export function useZoneInfoOutletContext() {
    return useOutletContext<ZoneInfoOutletContext>();
}

function ZoneInfoOutlet({ context }: { context: ZoneInfoOutletContext }) {
    return <Outlet context={context} />;
}

type ZoneInfoOutletContext = {
    zoneInfo?: ZoneInfo;
};
